import { observer } from 'mobx-react';
import React, { Fragment, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import Modal, { IModalProps } from 'components/Modal';
import GrowTransition from 'components/Modal/animations/grow';

import { IUseToggle } from '@common/helpers/useToggle';
import { INotificationModel } from '@common/models/Notifications/Notification';

import { StyledMediaTypography } from './styled';

interface PropsType extends Omit<IModalProps, 'open' | 'onClose'> {
	controls: IUseToggle;
}

function NotificationModal({ controls, ...props }: PropsType) {
	const notification = controls?.params?.notification as INotificationModel;
	const navigate = useNavigate();

	const handleClose = () => {
		notification.read();
		controls.onClose();
	};

	const parsedContent = useMemo(() => {
		const tempDiv = document.createElement('div');
		tempDiv.innerHTML = notification.message;

		return Array.from(tempDiv.childNodes).map((node, index) => {
			if (
				node.nodeName === 'BR' ||
				(node.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
			) {
				return null;
			}

			if (node.nodeName === 'A' && node instanceof HTMLAnchorElement) {
				const href = node.getAttribute('href') || '#';
				const className = node.getAttribute('class') || '';

				if (className.includes('link-button')) {
					return (
						<a
							key={index}
							href={href}
							className={className}
							onClick={(event) => {
								event.preventDefault();
								handleClose();
								navigate(href);
							}}
						>
							{node.innerHTML}
						</a>
					);
				}
			}

			if (node.nodeName === 'IMG' && node instanceof HTMLImageElement) {
				return (
					<img key={index} src={node.getAttribute('src') || ''} className={node.className} alt="" />
				);
			}

			if (node.nodeType === Node.TEXT_NODE) {
				return <Fragment key={index}>{node.textContent}</Fragment>;
			}

			const htmlElement = node as HTMLElement;
			return React.createElement(
				htmlElement.tagName.toLowerCase(),
				{ key: index, className: htmlElement.className },
				htmlElement.innerHTML,
			);
		});
	}, [notification.message]);

	return (
		<Modal
			PaperProps={{
				sx: {
					maxWidth: '500px',
				},
			}}
			title={notification.title}
			TransitionComponent={GrowTransition}
			transitionDuration={500}
			open={controls.isOpen}
			onClose={handleClose}
			{...props}
		>
			<StyledMediaTypography variant="body1" component="div">
				{parsedContent}
			</StyledMediaTypography>
		</Modal>
	);
}

export default observer(NotificationModal);
